/**
 * A learner's public page (components/profile/PublicProfile.tsx).
 *
 * See web/src/lib/strings/index.ts for how this file is shaped and why, and
 * docs/i18n.md for the Arabic glossary every value here must follow. Neither
 * object may be `as const`: `ar` is typed against `en`, and literal types
 * would make it have to repeat the English words.
 *
 * ## The register is different here, and deliberately
 *
 * Everywhere else the product speaks to a learner in the second person. This
 * page is read by a third party — somebody who was sent a link and has no
 * reason to trust whoever sent it — so it speaks about the learner, and every
 * sentence on it has one job: to say precisely what the product is vouching
 * for and precisely what it is not. A page that oversells is worth nothing to
 * the learner it is about, because the first reader who checks stops believing
 * the rest of it.
 *
 * ## What this page is for, which decides what is said first
 *
 * It is the artifact a learner shows somebody who is deciding about them — a
 * hiring manager reading a link off a CV or a profile, who has thirty seconds
 * and has never heard of this product. That reader needs three things in that
 * order, and the catalogue is ordered to match:
 *
 *   1. **A number they can hold.** `worlds` leads, because a world is a
 *      subject — "Recovery", "Team Git" — and a level number is an internal
 *      address. XP is not the headline and has not been since this page was
 *      repositioned: it is the product's own currency, it means nothing to an
 *      outside reader, and putting a game's score where the claim goes invites
 *      exactly the dismissal this page exists to avoid.
 *   2. **Why it is worth believing.** `summary` and `how` are the whole
 *      argument, and they are the three properties that separate this from a
 *      certificate of attendance: the work started from a repository nobody
 *      else has, it was graded on what was produced rather than on what was
 *      typed, and nothing helped. Each is a fact about the machinery
 *      (docs/checkpoints.md), not a claim about the person.
 *   3. **Where it stops.** `ceiling`, `provenance`, and the `artifact` /
 *      `learner` pair. None of those is an apology; they are the reason the
 *      sentences above them mean anything. `noneWhy` is in the same family:
 *      an empty page is a fact about this product's coverage, not a verdict on
 *      a learner.
 *
 * The one word this page will not say is "certified". `provenance` is the
 * reason: what happened is that this deployment's own server accepted an
 * artifact, and a reader who works out that "certified" was reaching stops
 * believing the checkable sentences too. A certificate's own name is the one
 * place the word can appear — `certificate.names` owns those, and the page
 * that checks one says exactly what it claims (strings/certificatePage.ts) —
 * and `certificates` below only names them and links to that page.
 */

import { ltr, num } from '../format';

export const en = {
  /**
   * The tab's own title.
   *
   * Most of what a pasted link looks like in a bookmark bar, a history list or
   * a second tab — and the title of the card a link shared into a chat or a
   * feed unfurls as, which the Worker writes from this same function
   * (worker/profile-card.ts). So it spends its middle on the claim rather than
   * on the product's name twice.
   */
  tab: (name: string) => `${name} · Confirmed record · EngineerOS`,
  /* What this page is, said once at the top. */
  label: 'Confirmed record',
  memberSince: (date: string) => `Member since ${date}`,
  loading: 'Loading…',

  /**
   * The headline, in worlds.
   *
   * A world is the largest unit this product can confirm and the only one that
   * survives contact with a reader who does not have the curriculum in front
   * of them. One sentence, next to the name, before any explanation: a reader
   * who takes in nothing else should still have taken in a number and a word.
   */
  worlds: (n: number) => (n === 1 ? '1 world confirmed' : `${num(n, 'en')} worlds confirmed`),

  /**
   * The sentence the rest of the page rests on.
   *
   * It names the mechanism — a checkpoint, graded by a server — because every
   * later claim is downstream of that one fact, and a reader who does not have
   * it reads the badges below as a course's own score for itself.
   */
  summary:
    'Each world below was confirmed by a checkpoint: one problem that puts together everything the levels of that world taught, graded by a server rather than by this learner’s browser.',

  /**
   * The three properties, as three lines rather than a paragraph.
   *
   * A paragraph here would be read by nobody it is written for. These are the
   * differences between this page and a certificate of completion, they are
   * each a fact about how the grading works, and a reader skimming three terms
   * in bold has still received the argument.
   *
   * `unaided` is worded narrowly on purpose. Checkpoints carry no hint ladder
   * — that is a rule in docs/checkpoints.md and it is checkable — but nothing
   * here observed the room the learner was sitting in, and a page that implied
   * otherwise would be making the one claim this product cannot support.
   */
  how: {
    label: 'What a confirmed world means',
    own: {
      term: 'Their own repository',
      body: 'A checkpoint starts from a repository generated for this learner alone, so a finished workspace taken from somebody else does not pass as theirs.',
    },
    state: {
      term: 'Graded on the result',
      body: 'The server reads the repository that was left behind — branches, history, the working tree, the tests — and never the commands typed to get there.',
    },
    unaided: {
      term: 'No hints',
      body: 'A checkpoint carries no hint ladder and no walkthrough. The learner directs designed agents, judges their proposals, and finishes what they leave.',
    },
  },

  /**
   * The certificates this learner holds, each a link to the page that checks
   * it. Listed rather than described: what a certificate claims is said once,
   * on its own page, where a reader deciding whether to believe it is already
   * looking.
   */
  certificates: {
    label: 'Certificates',
    issued: (date: string) => `Issued ${date}`,
    check: 'Check it',
  },

  confirmed: {
    label: 'Confirmed by the server',
    what: 'Each row is one checkpoint this learner submitted, graded in an isolated container. The award is fixed by the course and granted by the server; nothing a browser reports can create one.',
    on: (date: string) => `Confirmed ${date}`,
    xp: (n: number) => `${num(n, 'en')} XP confirmed`,
    /**
     * Under a checkpoint in lib/checkpoint.ts's MEASURED_AGAINST_A_MODEL. The
     * measurement is what makes this badge worth more than a pass, so it is
     * stated as what was done, with its count, and nothing larger — and the
     * model by its exact version and the day, UTC, so it stays true.
     */
    measured: (model: string, modelId: string, date: string, runs: number) =>
      `On ${date}, ${model} (${modelId}), working alone, was given this checkpoint ${num(runs, 'en')} times and did not pass it.`,
    /**
     * The worlds above the checkpoints that earned them.
     *
     * A world badge is the page's largest claim, so it is the one that has to
     * carry its own denominator, printed on every badge and never rolled up
     * into a footnote. The denominator changed shape when checkpoints replaced
     * per-level grading and the sentence had to change with it: "checked on 1
     * of 3 levels" was the honest wording while a badge rested on one teaching
     * level of a world and is an understatement now, because a checkpoint asks
     * for the whole world at once. So `coverage` names the world's size and
     * says how many checkpoints stand over it — one, today, for every world in
     * the table.
     *
     * `artifact` is the sentence for a badge whose levels start from the same
     * repository for everybody. It says what the server actually established —
     * that this work passes — and declines the larger sentence a badge invites
     * a reader to read, which is that this person did it. `learner` is the
     * stronger sentence, and the `salted` flag on the badge decides which is
     * shown rather than a choice compiled into the page. Every checkpoint is
     * salted today, so `learner` is what renders; `artifact` stays because the
     * flag is per level and a checkpoint added without a per-learner seed has
     * to be able to say the smaller thing.
     */
    badges: {
      label: 'Worlds confirmed',
      coverage: (checked: number, total: number) =>
        checked === 1
          ? `One checkpoint over this world’s ${num(total, 'en')} levels`
          : `${num(checked, 'en')} checkpoints over this world’s ${num(total, 'en')} levels`,
      artifact:
        'Every level starts from the same repository for every learner, so a confirmed world means this work was submitted and passed the server’s checks. It does not establish who produced it.',
      learner:
        'These checkpoints start from a repository generated for this learner, so the work behind a confirmed world could not have been copied from another account.',
    },
    /**
     * The honest ceiling, in the units the page is already speaking.
     *
     * Both numbers are counted from the badge table at render time rather than
     * written down here, for the reason the badges are derived rather than
     * stored: a sentence about coverage that somebody has to remember to edit
     * is a sentence that will one day be wrong on a stranger's screen.
     */
    ceiling: (worlds: number, levels: number) =>
      `Checkpoints exist for ${num(worlds, 'en')} worlds, covering ${num(levels, 'en')} levels of the course. Everything else a learner does here is practice, graded in their own browser, and practice is not shown on this page.`,
    none: 'No confirmed checkpoints yet.',
    noneWhy:
      'This page shows only work a server checked, so an empty page is not a statement about what this learner has done.',
  },

  /**
   * What this page is, for the reader who wants to know who is vouching.
   *
   * Quiet, at the foot, next to the contract version. It is the true shape of
   * the claim — this deployment says its server accepted this — and stating it
   * plainly is worth more than the word it declines to use.
   */
  provenance:
    'This page is EngineerOS reporting what its own grading server accepted. It is not a certificate issued by anybody else.',

  /* A link that does not open onto anybody. */
  missing: {
    title: 'No page here',
    body: 'This link does not open a learner page. It may have been mistyped, or the account may have been closed.',
  },

  /* The one thing this page sells, at the foot of it. */
  cta: 'See the course',
  ctaWhy:
    'EngineerOS teaches software engineering by directing AI agents, on a real Linux machine inside the browser tab. Its checkpoints are graded on a server, which is what this page reports.',
};

/**
 * Worlds, counted in Arabic.
 *
 * The dual is a form, not a number with a word after it, and three to ten take
 * a broken plural where eleven and up take the accusative singular. The levels
 * count below has the same shape for the same reason. Written out here rather
 * than reached for from a library because these are the only two counts on
 * this page and a pluralisation dependency for two sentences is not a trade
 * this bundle makes (docs/i18n.md §4).
 */
const worldsAr = (n: number) =>
  n === 1 ? 'عالم واحد مؤكَّد'
    : n === 2 ? 'عالمان مؤكَّدان'
    : n <= 10 ? `${num(n, 'ar')} عوالم مؤكَّدة`
    : `${num(n, 'ar')} عالمًا مؤكَّدًا`;

const worldsPlainAr = (n: number) =>
  n === 1 ? 'عالم واحد' : n === 2 ? 'عالمين' : n <= 10 ? `${num(n, 'ar')} عوالم` : `${num(n, 'ar')} عالمًا`;

const levelsAr = (n: number) =>
  n === 1 ? 'مستوى واحدًا' : n === 2 ? 'مستويين' : n <= 10 ? `${num(n, 'ar')} مستويات` : `${num(n, 'ar')} مستوى`;

/** `ثلاث مرات` — how many blind runs a model was given (docs/i18n.md §4). */
const timesAr = (n: number) =>
  n === 1 ? 'مرة واحدة' : n === 2 ? 'مرتين' : n <= 10 ? `${num(n, 'ar')} مرات` : `${num(n, 'ar')} مرة`;

export const ar: typeof en = {
  tab: (name: string) => `${name} · سجلّ مؤكَّد · EngineerOS`,
  label: 'سجلّ مؤكَّد',
  memberSince: (date: string) => `عضو منذ ${date}`,
  loading: 'جارٍ التحميل…',

  worlds: worldsAr,

  summary:
    'أكّدت كلَّ عالم في الأسفل محطةٌ: مسألة واحدة تجمع كل ما علّمته مستويات ذلك العالم، يفحصها خادم لا متصفّح هذا المتعلّم.',

  how: {
    label: 'ما الذي يعنيه عالم مؤكَّد',
    own: {
      term: 'مستودعه وحده',
      body: 'تبدأ المحطة من مستودع أُنشئ لهذا المتعلّم وحده، فمساحة عمل منتهية مأخوذة من شخص آخر لا تمرّ باسمه.',
    },
    state: {
      term: 'الفحص على النتيجة',
      body: 'يقرأ الخادم المستودع الذي تُرك خلفه — الفروع والتاريخ وشجرة العمل والاختبارات — ولا يقرأ الأوامر التي كُتبت للوصول إليه.',
    },
    unaided: {
      term: 'بلا تلميحات',
      body: 'لا تحمل المحطة سُلّم تلميحات ولا شرحًا خطوة بخطوة. يوجّه المتعلّم وكلاء مصمَّمين، ويحكم على ما يقترحونه، ويُكمل ما يتركونه.',
    },
  },

  certificates: {
    label: 'الشهادات',
    issued: (date: string) => `صدرت في ${date}`,
    check: 'تحقّق منها',
  },

  confirmed: {
    label: 'مؤكَّد من الخادم',
    what: 'كل صفّ محطةٌ أرسلها هذا المتعلّم وفُحصت داخل حاوية معزولة. المسار هو من يحدّد النقاط والخادم هو من يمنحها، ولا شيء يرسله المتصفّح يستطيع أن ينشئ واحدة.',
    on: (date: string) => `تأكّد في ${date}`,
    xp: (n: number) => `${num(n, 'ar')} نقطة خبرة مؤكَّدة`,
    measured: (model, modelId, date, runs) =>
      `في ${date} أُعطيت هذه المحطة للنموذج ${ltr(model)} (${ltr(modelId)}) وهو يعمل وحده ${timesAr(runs)}، فلم يجتزها في أيٍّ منها.`,
    badges: {
      label: 'العوالم المؤكَّدة',
      coverage: (checked: number, total: number) =>
        checked === 1
          ? `محطة واحدة على ${levelsAr(total)} في هذا العالم`
          : checked === 2
            ? `محطتان على ${levelsAr(total)} في هذا العالم`
            : `${num(checked, 'ar')} محطات على ${levelsAr(total)} في هذا العالم`,
      artifact:
        'تبدأ كل المستويات من المستودع نفسه لدى جميع المتعلّمين، لذا يعني العالم المؤكَّد أن هذا العمل أُرسل واجتاز فحوص الخادم. وهو لا يثبت هويّة من أنجزه.',
      learner:
        'تبدأ هذه المحطات من مستودع أُنشئ لهذا المتعلّم وحده، فالعمل وراء العالم المؤكَّد لا يمكن أن يكون منسوخًا من حساب آخر.',
    },
    ceiling: (worlds: number, levels: number) =>
      `توجد محطات لـ${worldsPlainAr(worlds)}، تغطّي ${levelsAr(levels)} من المسار. وما عدا ذلك تدريب يُفحص في متصفّح المتعلّم نفسه، والتدريب لا يظهر في هذه الصفحة.`,
    none: 'لا محطات مؤكَّدة بعد.',
    noneWhy: 'لا تعرض هذه الصفحة إلا عملًا فحصه خادم، فصفحة فارغة ليست حكمًا على ما أنجزه هذا المتعلّم.',
  },

  provenance: 'هذه الصفحة هي EngineerOS يخبر بما قبِله خادمه هو. وليست شهادة صادرة عن جهة أخرى.',

  missing: {
    title: 'لا صفحة هنا',
    body: 'هذا الرابط لا يفتح صفحة متعلّم. قد يكون في كتابته خطأ، أو يكون الحساب قد أُغلق.',
  },

  cta: 'اطّلع على المسار',
  ctaWhy: `‏EngineerOS يعلّم هندسة البرمجيات عبر توجيه وكلاء الذكاء الاصطناعي، على جهاز لينكس حقيقي داخل تبويب المتصفّح. وتُفحص محطاته على خادم، وهو ما تنقله هذه الصفحة.`,
};

export const de: typeof en = {
  tab: (name) => `${name} · Bestätigter Nachweis · EngineerOS`,
  label: 'Bestätigter Nachweis',
  memberSince: (date) => `Dabei seit ${date}`,
  loading: 'Wird geladen …',
  worlds: (n) => (n === 1 ? '1 Welt bestätigt' : `${num(n, 'de')} Welten bestätigt`),
  summary: 'Jede Welt unten wurde durch einen Checkpoint bestätigt: eine Aufgabe, die alles zusammenbringt, was die Level dieser Welt gelehrt haben, bewertet von einem Server statt vom Browser dieser Person.',
  how: {
    label: 'Was eine bestätigte Welt bedeutet',
    own: {
      term: 'Ein eigenes Repository',
      body: 'Ein Checkpoint beginnt mit einem Repository, das allein für diese Person erzeugt wurde. Ein fertiger Arbeitsstand von jemand anderem geht also nicht als ihrer durch.',
    },
    state: {
      term: 'Bewertet wird das Ergebnis',
      body: 'Der Server liest das Repository, das zurückgelassen wurde – Branches, Historie, Arbeitsverzeichnis, Tests – und nie die Befehle, die dorthin geführt haben.',
    },
    unaided: {
      term: 'Keine Hinweise',
      body: 'Ein Checkpoint hat keine Hinweisleiter und keine Schritt-für-Schritt-Anleitung. Die lernende Person leitet vorbereitete Agenten an, beurteilt ihre Vorschläge und erledigt, was sie liegen lassen.',
    },
  },
  certificates: {
    label: 'Zertifikate',
    issued: (date) => `Ausgestellt am ${date}`,
    check: 'Prüfen',
  },
  confirmed: {
    label: 'Vom Server bestätigt',
    what: 'Jede Zeile ist ein Checkpoint, den diese Person eingereicht hat, bewertet in einem isolierten Container. Die Auszeichnung legt der Kurs fest, vergeben wird sie vom Server; nichts, was ein Browser meldet, kann eine erzeugen.',
    on: (date) => `Bestätigt am ${date}`,
    xp: (n) => `${num(n, 'de')} XP bestätigt`,
    measured: (model, modelId, date, runs) =>
      `Am ${date} hat ${model} (${modelId}), allein arbeitend, diesen Checkpoint ${num(runs, 'de')}-mal bekommen und nicht bestanden.`,
    badges: {
      label: 'Bestätigte Welten',
      coverage: (checked, total) =>
        checked === 1
          ? `Ein Checkpoint über die ${num(total, 'de')} Level dieser Welt`
          : `${num(checked, 'de')} Checkpoints über die ${num(total, 'de')} Level dieser Welt`,
      artifact: 'Jedes Level beginnt für alle mit demselben Repository. Eine bestätigte Welt heißt also, dass diese Arbeit eingereicht wurde und die Prüfungen des Servers bestanden hat. Wer sie gemacht hat, belegt das nicht.',
      learner: 'Diese Checkpoints beginnen mit einem Repository, das für diese Person erzeugt wurde. Die Arbeit hinter einer bestätigten Welt kann also nicht von einem anderen Konto kopiert worden sein.',
    },
    ceiling: (worlds, levels) =>
      `Checkpoints gibt es für ${num(worlds, 'de')} Welten, die ${num(levels, 'de')} Level des Kurses abdecken. Alles andere, was man hier tut, ist Übung, bewertet im eigenen Browser, und Übung wird auf dieser Seite nicht gezeigt.`,
    none: 'Noch keine bestätigten Checkpoints.',
    noneWhy: 'Diese Seite zeigt nur Arbeit, die ein Server geprüft hat. Eine leere Seite sagt also nichts darüber, was diese Person getan hat.',
  },
  provenance: 'Auf dieser Seite berichtet EngineerOS, was sein eigener Bewertungsserver akzeptiert hat. Es ist kein Zertifikat, das jemand anderes ausgestellt hat.',
  missing: {
    title: 'Hier ist keine Seite',
    body: 'Dieser Link öffnet keine Seite einer Person. Vielleicht wurde er falsch abgetippt, oder das Konto wurde geschlossen.',
  },
  cta: 'Zum Kurs',
  ctaWhy: 'EngineerOS lehrt Softwareentwicklung, indem man KI-Agenten anleitet, auf einer echten Linux-Maschine im Browser-Tab. Seine Checkpoints werden auf einem Server bewertet, und genau darüber berichtet diese Seite.',
};

export const ja: typeof en = {
  tab: (name: string) => `${name} · 検証済みの記録 · EngineerOS`,
  label: '検証済みの記録',
  memberSince: (date: string) => `${date}から利用`,
  loading: '読み込み中…',
  worlds: (n: number) => `${num(n, 'ja')}ワールドを検証済み`,
  summary: '下の各ワールドは、チェックポイントによって検証されました。チェックポイントは、そのワールドのレベルで学んだことすべてを組み合わせた1つの課題で、この学習者のブラウザーではなくサーバーが採点します。',
  how: {
    label: '検証済みのワールドとは',
    own: {
      term: '専用のリポジトリ',
      body: 'チェックポイントは、この学習者のためだけに生成されたリポジトリから始まります。そのため、他人の完成済みワークスペースを持ってきても、その人の作業としては合格しません。',
    },
    state: {
      term: '結果にもとづく採点',
      body: 'サーバーが読むのは、最後に残されたリポジトリ(ブランチ、履歴、作業ツリー、テスト)です。そこに至るまでに入力したコマンドは読みません。',
    },
    unaided: {
      term: 'ヒントなし',
      body: 'チェックポイントには、ヒントの段階も手順の解説もありません。学習者は用意されたエージェントに指示を出し、その提案を判断し、エージェントが残した作業を仕上げます。',
    },
  },
  certificates: {
    label: '修了証',
    issued: (date: string) => `${date}に発行`,
    check: '確認する',
  },
  confirmed: {
    label: 'サーバーが検証済み',
    what: '各行は、この学習者が提出し、隔離されたコンテナで採点された1つのチェックポイントです。獲得できるものはコースが定め、サーバーが付与します。ブラウザーが何を報告しても、それを作り出すことはできません。',
    on: (date: string) => `${date}に検証`,
    xp: (n: number) => `${num(n, 'ja')} XP検証済み`,
    measured: (model: string, modelId: string, date: string, runs: number) =>
      `${date}に、${model}(${modelId})が単独でこのチェックポイントに${num(runs, 'ja')}回挑戦しましたが、合格しませんでした。`,
    badges: {
      label: '検証済みのワールド',
      coverage: (checked: number, total: number) =>
        `このワールドの${num(total, 'ja')}レベルに対して、${num(checked, 'ja')}個のチェックポイント`,
      artifact: 'どのレベルも、全員が同じリポジトリから始まります。したがって、検証済みのワールドが示すのは、この作業が提出され、サーバーのチェックに合格したことだけです。誰が作ったかまでは証明しません。',
      learner: 'これらのチェックポイントは、この学習者のために生成されたリポジトリから始まります。したがって、検証済みのワールドの作業を、別のアカウントからコピーすることはできません。',
    },
    ceiling: (worlds: number, levels: number) =>
      `チェックポイントがあるのは${num(worlds, 'ja')}ワールドで、コースの${num(levels, 'ja')}レベルをカバーしています。ここでのそれ以外の取り組みはすべて練習で、自分のブラウザーで採点され、練習はこのページには表示されません。`,
    none: '検証済みのチェックポイントはまだありません。',
    noneWhy: 'このページに表示されるのは、サーバーがチェックした作業だけです。そのため、ページが空でも、この学習者が何をしたかについては何も言えません。',
  },
  provenance: 'このページは、EngineerOS自身の採点サーバーが受理した内容をEngineerOSが報告しているものです。第三者が発行した修了証ではありません。',
  missing: {
    title: 'このページはありません',
    body: 'このリンクでは学習者のページを開けません。入力ミスか、アカウントが閉鎖された可能性があります。',
  },
  cta: 'コースを見る',
  ctaWhy: 'EngineerOSは、ブラウザーのタブの中の本物のLinuxマシンで、AIエージェントに指示を出しながらソフトウェアエンジニアリングを学ぶコースです。チェックポイントはサーバーで採点され、このページが報告しているのはその結果です。',
};
